import React, { useEffect, useState } from "react";
import {
  ActivityIndicator,
  Modal,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useTranslation } from "../../../utils/useTranslation";

interface Props {
  shipment: any;
  onDownload: (fileNames: string[]) => Promise<void>;
  onClose: () => void;
}

// Lists the files behind one container — the 装柜清单 it was converted from and any
// 胶片明细 filed against it — so the selected ones can be downloaded as the factory
// sent them.
const ChinaShipmentFilesModal: React.FC<Props> = ({ shipment, onDownload, onClose }) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [downloading, setDownloading] = useState<boolean>(false);

  useEffect(() => {
    setSelected(new Set());
  }, [shipment]);

  const files: { fileName: string; label: string }[] = [
    ...(shipment?.sourceFileName
      ? [{ fileName: shipment.sourceFileName, label: t("packingListFileLabel") }]
      : []),
    ...[...(shipment?.attachments || [])]
      .sort(
        (a: any, b: any) =>
          new Date(b?.uploadedAt || 0).getTime() - new Date(a?.uploadedAt || 0).getTime(),
      )
      .map((a: any) => ({ fileName: a.fileName, label: t("filmLogFileLabel") })),
  ];

  const toggle = (fileName: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(fileName)) next.delete(fileName);
      else next.add(fileName);
      return next;
    });

  const handleDownload = async () => {
    if (!selected.size || downloading) return;
    setDownloading(true);
    try {
      await onDownload(files.map((f) => f.fileName).filter((f) => selected.has(f)));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal visible={!!shipment} transparent animationType="fade">
      <View style={styles.overlay}>
        <View style={styles.modal}>
          <Text style={styles.title}>{t("shipmentFilesTitle")}</Text>
          <View style={styles.containerBadge}>
            <Text style={styles.containerBadgeText}>{shipment?.containerNo}</Text>
          </View>

          {files.map((file) => {
            const checked = selected.has(file.fileName);
            return (
              <TouchableOpacity
                key={file.fileName}
                style={[styles.fileRow, checked && styles.fileRowChecked]}
                onPress={() => toggle(file.fileName)}
              >
                <MaterialCommunityIcons
                  name={checked ? "checkbox-marked" : "checkbox-blank-outline"}
                  size={22}
                  color={checked ? "rgb(47, 149, 220)" : "#9ca3af"}
                />
                <MaterialCommunityIcons name="file-excel-outline" size={22} color="#15803d" />
                <View style={styles.fileText}>
                  <Text style={styles.fileLabel}>{file.label}</Text>
                  <Text style={styles.fileName} numberOfLines={1}>
                    {file.fileName}
                  </Text>
                </View>
              </TouchableOpacity>
            );
          })}

          <View style={styles.footer}>
            <TouchableOpacity style={styles.closeBtn} onPress={onClose}>
              <Text style={styles.closeBtnText}>{t("cancel")}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.downloadBtn, (!selected.size || downloading) && styles.downloadBtnDisabled]}
              onPress={handleDownload}
              disabled={!selected.size || downloading}
            >
              {downloading ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <>
                  <MaterialCommunityIcons name="download" size={18} color="#fff" />
                  <Text style={styles.downloadBtnText}>
                    {t("downloadSelectedBtn").replace("{count}", String(selected.size))}
                  </Text>
                </>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default ChinaShipmentFilesModal;

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.4)",
    justifyContent: "center",
    alignItems: "center",
  },
  modal: {
    width: "90%",
    maxWidth: 480,
    backgroundColor: "#fff",
    borderRadius: 14,
    padding: 24,
    alignItems: "center",
  },
  title: {
    fontSize: 16,
    fontWeight: "700",
    color: "#111",
    textAlign: "center",
  },
  containerBadge: {
    marginTop: 10,
    marginBottom: 16,
    backgroundColor: "#f1f5f9",
    borderRadius: 8,
    paddingVertical: 6,
    paddingHorizontal: 14,
  },
  containerBadgeText: {
    fontSize: 13,
    fontWeight: "700",
    color: "#111827",
  },
  fileRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    width: "100%",
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginBottom: 8,
  },
  fileRowChecked: {
    borderColor: "rgb(47, 149, 220)",
    backgroundColor: "#f0f7fd",
  },
  fileText: {
    flex: 1,
  },
  fileLabel: {
    fontSize: 12,
    fontWeight: "700",
    color: "#374151",
  },
  fileName: {
    fontSize: 13,
    color: "#555",
  },
  footer: {
    flexDirection: "row",
    gap: 10,
    marginTop: 14,
    width: "100%",
  },
  closeBtn: {
    flex: 1,
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderRadius: 10,
    paddingVertical: 11,
    alignItems: "center",
    backgroundColor: "#fff",
  },
  closeBtnText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#374151",
  },
  downloadBtn: {
    flex: 1,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: 6,
    borderRadius: 10,
    paddingVertical: 11,
    backgroundColor: "rgb(47, 149, 220)",
  },
  downloadBtnDisabled: {
    opacity: 0.4,
  },
  downloadBtnText: {
    fontSize: 14,
    fontWeight: "700",
    color: "#fff",
  },
});
